<template>
  <div class="braintree-apm-container">

    <div v-if="isLoading" class="loading-indicator">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{$t('labels.Loading')}}</span>
      </div>
    </div>

    <div v-if="errorMessage" class="alert alert-danger" role="alert">
      {{ errorMessage }}
    </div>

    <!-- EPS Payment Button Container -->
    <div id="braintree-eps-button" class="payment-button-container"></div>

    <!-- Payment Information Display -->
    <div v-if="paymentInfo" class="payment-info mt-3">
      <div class="alert alert-info">
        <strong>{{ $t('checkout.PaymentAmount') }}</strong> {{ formatCurrency(cart.balance) }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { aoeBraintreeAPM } from './AoeBraintreeAPM'
import { getFunnelType } from '@/analytics/index'
import { CartPaymentTokenCreate } from '@/api/Cart'
import { GetOrderEntryConnectorUrl } from '@/stores/SettingsStore'
import { useStoreLogger } from '@/logger'
import { FormatCurrency } from '@/services/currencyFormatter'

const { t } = useI18n()
const logger = useStoreLogger()

// Props
const props = defineProps({
  cart: {
    type: Object,
    required: true
  },
  systemKeyword: {
    type: String,
    required: true
  },
  name: {
    type: String,
    required: true
  }
})

// Reactive state
const isLoading = ref(true)
const errorMessage = ref('')
const paymentInfo = ref<{amount: number, currency: string} | null>(null)

// Initialize the APM component
onMounted(async () => {
  logger.info('BraintreeAPMPaymentEntry mounted', { props })
  try {
    isLoading.value = true
    errorMessage.value = ''

    // Get client token from API
    const response = await CartPaymentTokenCreate({
      aoeCartIdToken: props.cart.aoeCartIdToken,
      systemKeyword: props.systemKeyword,
      charge: props.cart.balance
    })

    if (response.item.ClientToken) {
      // Configure the APM component
      aoeBraintreeAPM.clientToken = response.item.ClientToken
      aoeBraintreeAPM.paymentValues.paymentAmount = props.cart.balance.toString()
      aoeBraintreeAPM.paymentValues.currencyCode = 'EUR'
      aoeBraintreeAPM.createPaymentUrl = await GetOrderEntryConnectorUrl('CartPaymentCreate')
      aoeBraintreeAPM.sourceValues.aOECartIdToken = props.cart.aoeCartIdToken
      aoeBraintreeAPM.sourceValues.systemKeyword = props.systemKeyword
      aoeBraintreeAPM.cartFunnelType = getFunnelType(props.cart.orderTypeId, props.cart.eventId)

      // Initialize the APM component
      aoeBraintreeAPM.Initialize()

      // Set payment info
      paymentInfo.value = {
        amount: props.cart.balance,
        currency: 'EUR'
      }
    } else {
      errorMessage.value = t('checkout.NotSuppoted')
      logger.error('BraintreeAPMPaymentEntry', 'No client token received')
    }
  } catch (error) {
    errorMessage.value = t('checkout.NotSuppoted')
    logger.error('BraintreeAPMPaymentEntry', 'Error initializing APM component', error)
  } finally {
    isLoading.value = false
  }
})

// Watch for payment messages
onMounted(() => {
  window.addEventListener('message', handleMessage)

  return () => {
    window.removeEventListener('message', handleMessage)
  }
})

// Handle payment messages
function handleMessage(event: MessageEvent) {
  const { messageType, messageData } = event.data || {}

  if (messageType === 'aoepaymentcreated') {
    logger.info('BraintreeAPMPaymentEntry', 'Payment created', messageData)
    // You can emit an event to notify parent components
  } else if (messageType === 'aoepaymentcreationerror') {
    logger.error('BraintreeAPMPaymentEntry', 'Payment creation error', messageData)
    errorMessage.value = messageData?.responseStatus?.message || t('checkout.NotSuppoted')
  } else if (messageType === 'aoepaymentcreation3dsusercanceled') {
    logger.warn('BraintreeAPMPaymentEntry', '3D Secure user canceled', messageData)
  }
}

// Format currency using centralized formatter
function formatCurrency(amount: number): string {
  return FormatCurrency(amount, null) || amount.toFixed(2)
}
</script>

<style lang="scss" scoped>
#braintree-eps-button {
  min-height: 3rem;
}

.loading-indicator {
  display: flex;
  justify-content: center;
  padding: 2rem 0;
}

.payment-button-container {
  margin-bottom: 1.5rem;
}

.payment-button-container button {
  @include typography-body;
  @include font-weight-medium;
  width: 100%;
  padding: 0.75rem;
}
</style>
